import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { Tutorial, youtubeEmbedUrl, youtubePreview, youtubeWatchUrl } from './tutorials';

import styles from './styles.module.scss';

const playIcon = new URL('../../../img/main-page/play.svg', import.meta.url).href;

interface TutorialCardProps {
  tutorial: Tutorial;
  /** Номер ролика в курсе, он же порядок просмотра */
  number: number;
}

const TutorialCard: React.FC<TutorialCardProps> = ({ tutorial, number }) => {
  const { t } = useTranslation();
  // Плеер грузим только после клика: пять iframe сразу утяжелили бы страницу
  const [playing, setPlaying] = useState(false);

  const title = t(`tutorials.items.${tutorial.id}.title`);
  const description = t(`tutorials.items.${tutorial.id}.description`);
  const isPublished = Boolean(tutorial.youtubeId);

  return (
    <article className={cn(styles.card, { [styles.cardSoon]: !isPublished })}>
      <div className={styles.preview}>
        {playing && tutorial.youtubeId ? (
          <iframe
            className={styles.player}
            src={youtubeEmbedUrl(tutorial.youtubeId)}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <>
            {tutorial.youtubeId && (
              <img
                className={styles.previewImage}
                src={youtubePreview(tutorial.youtubeId)}
                alt=""
                loading="lazy"
              />
            )}
            <span className={styles.number}>{number}</span>
            {isPublished ? (
              <button
                type="button"
                className={styles.playButton}
                onClick={() => setPlaying(true)}
                aria-label={`${t('tutorials.watch')}: ${title}`}
              >
                <img src={playIcon} alt="" className={styles.playIcon} />
              </button>
            ) : (
              <span className={styles.soonBadge}>{t('tutorials.soon')}</span>
            )}
            {tutorial.duration && <span className={styles.duration}>{tutorial.duration}</span>}
          </>
        )}
      </div>

      <div className={styles.cardBody}>
        <h2 className={styles.cardTitle}>{title}</h2>
        <p className={styles.cardText}>{description}</p>
        {isPublished && tutorial.youtubeId && (
          // Ссылка на сам YouTube нужна и для тех, кто смотрит с телефона в приложении,
          // и для поисковиков: в разметке остаётся обычный href, а не только кнопка плеера
          <a
            className={styles.cardLink}
            href={youtubeWatchUrl(tutorial.youtubeId)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('tutorials.openOnYoutube')}
          </a>
        )}
      </div>
    </article>
  );
};

export default TutorialCard;
